import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Store, ArrowLeft, Eye, EyeOff, ShieldCheck, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { platformApi } from '@/utils/platformApi';
import { getPasswordComplexityErrors, PASSWORD_HINT } from '@/utils/passwordValidation';

export default function PlatformLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials' | 'mfa' | 'force_password_change'
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [mfaState, setMfaState] = useState({ challengeId: '', code: '', emailHint: '' });
  const [newPassword, setNewPassword] = useState({ value: '', confirm: '' });
  const [pendingResult, setPendingResult] = useState(null);

  const goToDestination = (result) => {
    const { user, redirect_url, is_platform_user } = result;
    toast.success(`Welcome back, ${user.name}!`);
    navigate(is_platform_user ? '/platform/admin' : redirect_url);
  };

  const handleAuthResult = (result) => {
    if (result.mfa_required) {
      setMfaState({ challengeId: result.challenge_id, code: '', emailHint: result.email_hint || '' });
      setStep('mfa');
      return;
    }
    if (result.user?.must_change_password) {
      setPendingResult(result);
      setStep('force_password_change');
      return;
    }
    goToDestination(result);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      setLoading(true);
      const response = await platformApi.post('/platform/auth/login', {
        email: formData.email,
        password: formData.password
      });
      handleAuthResult(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 429) {
        toast.error('Too many login attempts. Please try again later.');
      } else if (error.response?.status === 400 && detail?.includes('not associated')) {
        toast.error('Your account is not associated with any store. Please contact support.');
      } else {
        toast.error(detail || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    if (!mfaState.code || mfaState.code.length !== 6) {
      toast.error('Enter the 6-digit code sent to your email');
      return;
    }
    try {
      setLoading(true);
      const response = await platformApi.post('/platform/auth/mfa/verify-login', {
        challenge_id: mfaState.challengeId,
        code: mfaState.code
      });
      handleAuthResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      const response = await platformApi.post('/platform/auth/mfa/resend-login-code', {
        challenge_id: mfaState.challengeId
      });
      setMfaState((prev) => ({ ...prev, challengeId: response.data.challenge_id, code: '' }));
      toast.success('A new code has been sent to your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend code. Please sign in again.');
      setStep('credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForcePasswordChange = async (e) => {
    e.preventDefault();
    const pwErrors = getPasswordComplexityErrors(newPassword.value);
    if (pwErrors.length > 0) {
      toast.error(`Password must contain ${pwErrors.join(', ')}.`);
      return;
    }
    if (newPassword.value !== newPassword.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      await platformApi.post('/auth/change-password', {
        current_password: formData.password,
        new_password: newPassword.value
      });
      toast.success('Password updated successfully.');
      goToDestination(pendingResult);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F3EF] to-white flex flex-col">
      {/* Header */}
      <header className="border-b border-[#E8E4DC] bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#4A6741] rounded-lg flex items-center justify-center">
              <Store className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Golfshop.fit
            </span>
          </Link>
          <Link to="/" className="text-[#5A6B5C] hover:text-[#4A6741] flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl border border-[#E8E4DC] p-8 shadow-sm">
            {step === 'credentials' && (
              <>
                <h1
                  className="text-2xl font-bold text-[#2C3E2D] mb-2 text-center"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                  data-testid="login-title"
                >
                  Sign In
                </h1>
                <p className="text-[#5A6B5C] text-center mb-8">
                  Enter your email and password to continue
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="email" className="text-[#2C3E2D]">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1 border-[#E8E4DC]"
                      data-testid="login-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-[#2C3E2D]">Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        className="border-[#E8E4DC] pr-10"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6B5C] hover:text-[#4A6741]"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#4A6741] hover:bg-[#3d5636] text-white py-5"
                    data-testid="login-submit"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-[#5A6B5C] text-sm">
                    Don't have a store yet?{' '}
                    <Link to="/signup" className="text-[#4A6741] font-medium hover:underline">
                      Create one for free
                    </Link>
                  </p>
                </div>
              </>
            )}

            {step === 'mfa' && (
              <>
                <div className="h-12 w-12 bg-[#4A6741]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="h-6 w-6 text-[#4A6741]" />
                </div>
                <h1
                  className="text-2xl font-bold text-[#2C3E2D] mb-2 text-center"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                  data-testid="mfa-title"
                >
                  Enter Verification Code
                </h1>
                <p className="text-[#5A6B5C] text-center mb-8 text-sm">
                  We sent a 6-digit code to {mfaState.emailHint || 'your email'}
                </p>

                <form onSubmit={handleVerifyMfa} className="space-y-5">
                  <div>
                    <Label htmlFor="mfa-code" className="text-[#2C3E2D]">Verification Code</Label>
                    <Input
                      id="mfa-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      value={mfaState.code}
                      onChange={(e) => setMfaState(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '') }))}
                      className="mt-1 border-[#E8E4DC] text-center text-2xl tracking-[0.5em]"
                      data-testid="mfa-code-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#4A6741] hover:bg-[#3d5636] text-white py-5"
                    data-testid="mfa-verify-submit"
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>
                </form>

                <div className="mt-6 text-center space-y-2">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading}
                    className="text-[#4A6741] text-sm font-medium hover:underline disabled:opacity-50"
                    data-testid="mfa-resend-code-btn"
                  >
                    Resend code
                  </button>
                  <div>
                    <button
                      type="button"
                      onClick={() => setStep('credentials')}
                      className="text-[#5A6B5C] text-sm hover:text-[#4A6741]"
                      data-testid="mfa-back-btn"
                    >
                      Back to sign in
                    </button>
                  </div>
                </div>
              </>
            )}

            {step === 'force_password_change' && (
              <>
                <div className="h-12 w-12 bg-[#D4A55A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="h-6 w-6 text-[#D4A55A]" />
                </div>
                <h1
                  className="text-2xl font-bold text-[#2C3E2D] mb-2 text-center"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                  data-testid="force-password-change-title"
                >
                  Update Your Password
                </h1>
                <p className="text-[#5A6B5C] text-center mb-8 text-sm">
                  For security, please set a new password before continuing.
                </p>

                <form onSubmit={handleForcePasswordChange} className="space-y-5">
                  <div>
                    <Label htmlFor="new-password" className="text-[#2C3E2D]">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Enter a new password"
                      value={newPassword.value}
                      onChange={(e) => setNewPassword(prev => ({ ...prev, value: e.target.value }))}
                      className="mt-1 border-[#E8E4DC]"
                      data-testid="force-password-new-input"
                    />
                    <p className="text-xs text-[#5A6B5C] mt-1">{PASSWORD_HINT}</p>
                  </div>
                  <div>
                    <Label htmlFor="confirm-new-password" className="text-[#2C3E2D]">Confirm New Password</Label>
                    <Input
                      id="confirm-new-password"
                      type="password"
                      placeholder="Re-enter the new password"
                      value={newPassword.confirm}
                      onChange={(e) => setNewPassword(prev => ({ ...prev, confirm: e.target.value }))}
                      className="mt-1 border-[#E8E4DC]"
                      data-testid="force-password-confirm-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#4A6741] hover:bg-[#3d5636] text-white py-5"
                    data-testid="force-password-submit"
                  >
                    {loading ? 'Updating...' : 'Update Password & Continue'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
